import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { For, Show } from "solid-js";
import { IconArrowLeft, IconBot, IconUser } from "~/components/icons";
import { Markdown } from "~/components/markdown";
import { NotFound } from "~/components/not-found";
import { Button } from "~/components/ui/button";
import { getChatSessionDetailPageData } from "~/features/chat/chat-session-detail-page";
import { chatSessionResource } from "~/features/chat/chat.resource";
import { cn, formatDisplayDate } from "~/lib/utils";

export const Route = createFileRoute("/_dashboard/dashboard/chat/$sessionId")({
  staticData: {
    breadcrumb: (m) =>
      m.loaderData?.session?.title ?? `Session ${m.params.sessionId}`,
  },
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a chat session doesn't exist — e.g. it was
  // deleted, or an agent may have linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Chat session not found"
      message="This chat session doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/chat"
      homeLabel="Go to chat"
    />
  ),
  loader: async ({ context, params }) => {
    const data = await getChatSessionDetailPageData({
      data: { sessionId: params.sessionId },
    });
    // Seed the resource cache so the detail query resolves from cache on
    // first render (mirrors the document detail page pattern).
    chatSessionResource.seed(
      context.queryClient,
      params.sessionId,
      data.session,
    );
    return { session: data.session };
  },
  component: ChatSessionPage,
});

function ChatSessionPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  const sessionQuery = chatSessionResource.use(() => params().sessionId);
  const session = () => sessionQuery.data ?? data().session;

  chatSessionResource.seedIfEmpty(qc, params().sessionId, data().session);

  return (
    <div class="flex flex-col h-full max-w-3xl mx-auto p-6 gap-4">
      <div class="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void navigate({ to: "/dashboard/chat" })}
        >
          <IconArrowLeft class="size-4" />
        </Button>
        <div class="flex flex-col">
          <h1 class="text-lg font-semibold">{session().title}</h1>
          <p class="text-xs text-muted-foreground capitalize">
            {session().source} &middot;{" "}
            {formatDisplayDate(session().createdAt) ?? "-"}
          </p>
        </div>
      </div>

      <Show
        when={session().messages.length > 0}
        fallback={
          <div class="flex flex-col items-center justify-center flex-1 text-muted-foreground gap-3">
            <IconBot class="size-10 opacity-40" />
            <p class="text-sm">No messages in this session.</p>
          </div>
        }
      >
        <div class="flex flex-col gap-4 overflow-y-auto flex-1">
          <For each={session().messages}>
            {(message) => (
              <div
                class={cn(
                  "flex gap-3",
                  message.role === "user" ? "flex-row-reverse" : "flex-row",
                )}
              >
                <div
                  class={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-full",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  <Show
                    when={message.role === "assistant"}
                    fallback={<IconUser class="size-4" />}
                  >
                    <IconBot class="size-4" />
                  </Show>
                </div>
                <div
                  class={cn(
                    "rounded-lg px-4 py-2 max-w-[80%] text-sm",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  <Show
                    when={message.role === "assistant"}
                    fallback={<p>{message.content}</p>}
                  >
                    <Markdown content={message.content} />
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
